import type { DerivedSnapshot } from "@/lib/derivedIntel/derivedIntelligenceEngine";

/**
 * Confidence Engine — pure, deterministic confidence rating for any
 * derived insight or prediction. No I/O, no AI.
 *
 * Confidence rises with sample count, window stability, momentum consistency
 * and falls with high variance / abrupt narrative shifts.
 */

export type ConfidenceLevel =
  | "limited_sample"
  | "low"
  | "medium"
  | "high"
  | "strong_trend"
  | "volatile";

export interface ConfidenceRating {
  level: ConfidenceLevel;
  score: number; // 0..1
  label: string;
  reasons: string[];
}

const LABELS: Record<ConfidenceLevel, string> = {
  limited_sample: "LIMITED SAMPLE",
  low: "LOW CONFIDENCE",
  medium: "MEDIUM CONFIDENCE",
  high: "HIGH CONFIDENCE",
  strong_trend: "STRONG TREND",
  volatile: "VOLATILE MATCH",
};

export function rateConfidence(snap: DerivedSnapshot): ConfidenceRating {
  const w2 = snap.windows.w2m;
  const w5 = snap.windows.w5m;
  const reasons: string[] = [];

  if (w5.samples < 6) {
    return {
      level: "limited_sample",
      score: 0.15,
      label: LABELS.limited_sample,
      reasons: ["Fewer than 6 samples in 5-min window"],
    };
  }

  // Stability of momentum between windows (smaller delta = more stable).
  const momentumDelta = Math.abs(w2.avgMomentum - w5.avgMomentum);
  const pressureDelta = Math.abs(
    w2.avgPressureHome - w5.avgPressureHome,
  ) + Math.abs(w2.avgPressureAway - w5.avgPressureAway);

  const stability = clamp01(1 - momentumDelta * 0.8 - pressureDelta * 0.4);
  const sampleScore = clamp01(w5.samples / 30);
  const trendStrength = Math.min(
    1,
    Math.abs(w5.avgMomentum) + Math.abs(w5.territorialDominance) * 0.5,
  );

  const score = clamp01(stability * 0.5 + sampleScore * 0.25 + trendStrength * 0.25);

  if (momentumDelta > 0.55 || pressureDelta > 0.6) {
    reasons.push("High variance between 2m and 5m windows");
    return { level: "volatile", score, label: LABELS.volatile, reasons };
  }
  if (trendStrength > 0.7 && stability > 0.6) {
    reasons.push("Consistent momentum + territorial dominance");
    return { level: "strong_trend", score, label: LABELS.strong_trend, reasons };
  }
  if (score > 0.7) {
    reasons.push("Stable windows, sufficient samples");
    return { level: "high", score, label: LABELS.high, reasons };
  }
  if (score > 0.45) return { level: "medium", score, label: LABELS.medium, reasons };
  reasons.push("Weak or noisy signal");
  return { level: "low", score, label: LABELS.low, reasons };
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}